import { INPUT_TYPE, NOTIFICATION_VARIANT } from '../../../constants/types';
import { useForm } from '../../../hooks/useForm';
import { RECOMMENDATION_LIST_TYPE, RECOMMENDATION_LIST_VISIBILITY } from './interfaces';
import api from '../../../api/api';
import { handleRequestHelper } from '../../../utils/helpers';
import { useNotifications } from '../../../Providers/NotificationsProvider/NotificationsProvider';
import history from '../../../appHistory';

const DEFAULT_MIN_EVENTS_FOR_LIST = 3;

const minEventsForList = (type) =>
  type === RECOMMENDATION_LIST_TYPE.FEATURED_EVENT ? 1 : DEFAULT_MIN_EVENTS_FOR_LIST;

const eventCustomValidation = (value, { type }) => {
  if (type === RECOMMENDATION_LIST_TYPE.PHOTO_CAROUSEL) {
    const hasImages = value.every((event) => event.image);
    if (!hasImages) return 'Los eventos deben tener una imagen';
  }
  if (value.length < minEventsForList(type))
    return `La lista debe contener al menos ${minEventsForList(type)} eventos`;
  return '';
};

export const useRecommendationListForm = (initialList) => {
  const { showNotification } = useNotifications();
  const {
    handleInputChange,
    validateFormInputs,
    resetForm,
    formValues,
    formState,
    requiredStates,
  } = useForm(
    {
      type: {
        type: INPUT_TYPE.SELECT,
        value: initialList?.type || RECOMMENDATION_LIST_TYPE.BASIC_LIST,
      },
      title: {
        value: initialList?.title || '',
        required: ({ type }) => type !== RECOMMENDATION_LIST_TYPE.FLYER_CAROUSEL,
      },
      visibility: {
        type: INPUT_TYPE.SELECT,
        value: initialList?.visibility || RECOMMENDATION_LIST_VISIBILITY.ACTIVE,
      },
      description: {
        value: initialList?.description || '',
        required: ({ type }) =>
          type === RECOMMENDATION_LIST_TYPE.BASIC_LIST ||
          type === RECOMMENDATION_LIST_TYPE.PHOTO_LIST ||
          type === RECOMMENDATION_LIST_TYPE.THEMED_LIST ||
          type === RECOMMENDATION_LIST_TYPE.FEATURED_EVENT,
      },
      theme: {
        value: initialList?.theme || '',
        required: ({ type }) => type === RECOMMENDATION_LIST_TYPE.THEMED_LIST,
      },
      events: {
        value: initialList?.events || [],
        customValidation: eventCustomValidation,
      },
      image: {
        value: initialList?.image || '',
        required: ({ type }) =>
          type === RECOMMENDATION_LIST_TYPE.PHOTO_LIST ||
          type === RECOMMENDATION_LIST_TYPE.FEATURED_EVENT,
      },
    },
    []
  );

  const searchEvents = async (searchQuery) => {
    return handleRequestHelper({
      endpoint: () => api.getActiveEvents({ searchQuery }),
      onSuccess: ({ events }) => events,
    });
  };

  const handleEventSelect = (event) => {
    if (!event) return;

    const currentEvents = formState.events.value;
    const eventExists = currentEvents.some((e) => e.id === event.id);

    if (!eventExists) {
      handleInputChange({
        target: {
          name: 'events',
          value: [...currentEvents, event],
        },
      });
    }
  };

  const handleRemoveEvent = (eventId) => {
    const currentEvents = formState.events.value;

    handleInputChange({
      target: {
        name: 'events',
        value: currentEvents.filter(({ id }) => id !== eventId),
      },
    });
  };

  const formatFormValues = () => {
    const { type, title, description, visibility, theme, events, image } = formValues;
    const formattedEvents = events.map(({ id, image }) => ({
      id,
      image,
    }));

    const body = { type, visibility, events: formattedEvents };

    switch (type) {
      case RECOMMENDATION_LIST_TYPE.PHOTO_LIST:
        return { image, title, description, ...body };
      case RECOMMENDATION_LIST_TYPE.BASIC_LIST:
        return { description, title, ...body };
      case RECOMMENDATION_LIST_TYPE.THEMED_LIST:
        return { theme, title, description, ...body };
      case RECOMMENDATION_LIST_TYPE.FEATURED_EVENT:
        return { image, description, title, ...body };
      case RECOMMENDATION_LIST_TYPE.PHOTO_CAROUSEL:
        return { title, ...body };
      default:
        return body;
    }
  };

  const handleDragEnd = (items) => {
    handleInputChange({ target: { name: 'events', value: items } });
  };

  const handleCreateList = async () => {
    await handleRequestHelper({
      endpoint: () => api.createRecommendationList(formatFormValues()),
      onSuccess: () => {
        showNotification('Lista creada con éxito', NOTIFICATION_VARIANT.SUCCESS);
        resetForm();
      },
      onFailure: ({ errorMessage }) => {
        showNotification(`Error al crear lista: ${errorMessage}`, NOTIFICATION_VARIANT.ERROR);
      },
    });
  };

  const handleUpdateList = async () => {
    await handleRequestHelper({
      endpoint: () => api.updateRecommendationList(initialList.id, formatFormValues()),
      onSuccess: () => {
        showNotification('Lista actualizada con éxito', NOTIFICATION_VARIANT.SUCCESS);
        history.push('/backoffice/admin?tab=recommendationLists');
      },
    });
  };

  const handleSubmit = async () => {
    const isFormValid = validateFormInputs();
    if (!isFormValid) return;

    if (initialList) {
      await handleUpdateList();
    } else {
      await handleCreateList();
    }
  };

  const handleSetEventImage = (eventId, image) => {
    const currentEvents = formState.events.value;
    const eventIndex = currentEvents.findIndex((event) => event.id === eventId);
    currentEvents[eventIndex].image = image;

    handleInputChange({
      target: {
        name: 'events',
        value: currentEvents,
      },
    });
  };

  return {
    formState,
    handleInputChange,
    requiredStates,
    searchEvents,
    handleEventSelect,
    handleRemoveEvent,
    handleSubmit,
    handleDragEnd,
    handleSetEventImage,
  };
};
